// Asset Service for managing and caching assets
import missionBg from '../assets/img/mission.jpg';
import cropLogo from '../assets/img/crop logo.png';
import aimLogo from '../assets/img/AIM removebg-.png';
import aimLogoAlt from '../assets/img/AIM-removebg-.png';
import domusLogo from '../assets/img/DOMUS_FINAL_LOGO.png';
import defaultDoctor from '../assets/img/default-doctor.svg';
import patternSvg from '../assets/img/pattern.svg';

class AssetService {
  constructor() {
    this.cache = new Map();
    this.loadedAssets = new Set();
    this.imageCache = new Map();
    this.importedAssets = {
      missionBg,
      cropLogo,
      aimLogo,
      aimLogoAlt,
      domusLogo,
      defaultDoctor,
      patternSvg
    };
    this.initializeCache();
  }

  // Initialize cache and handle hard refresh
  initializeCache() {
    // Load existing cache from sessionStorage first
    this.loadFromSessionStorage();
    
    // Only clear cache on actual hard refresh (browser refresh button or Ctrl+F5)
    const navigation = performance.getEntriesByType('navigation')[0];
    const isHardRefresh = navigation && navigation.type === 'reload' && !sessionStorage.getItem('assetCache');
    
    if (isHardRefresh) {
      this.clearCache();
    }

    // Listen for beforeunload to save cache
    window.addEventListener('beforeunload', () => {
      this.saveToSessionStorage();
    });

    // Listen for page visibility change to save cache
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.saveToSessionStorage();
      }
    });

    // Save cache periodically to ensure persistence
    setInterval(() => {
      if (!this.isCacheEmpty()) {
        this.saveToSessionStorage();
      }
    }, 30000); // Save every 30 seconds
  }

  // Save cache to sessionStorage
  saveToSessionStorage() {
    try {
      const cacheData = {
        cache: Array.from(this.cache.entries()),
        loadedAssets: Array.from(this.loadedAssets),
        timestamp: Date.now()
      };
      sessionStorage.setItem('assetCache', JSON.stringify(cacheData));
      // Only log on first save or when cache is substantial
      if (this.cache.size > 0 && !this._hasLoggedSave) {
        console.log('Cache saved to sessionStorage');
        this._hasLoggedSave = true;
      }
    } catch (error) {
      console.warn('Failed to save cache to sessionStorage:', error);
    }
  }

  // Load cache from sessionStorage
  loadFromSessionStorage() {
    try {
      const cacheData = sessionStorage.getItem('assetCache');
      if (cacheData) {
        const parsed = JSON.parse(cacheData);
        this.cache = new Map(parsed.cache);
        this.loadedAssets = new Set(parsed.loadedAssets);
        console.log('Cache restored from sessionStorage');
        return true;
      }
    } catch (error) {
      console.warn('Failed to load cache from sessionStorage:', error);
    }
    return false;
  }

  // Force clear cache (manual method)
  forceClearCache() {
    this.clearCache();
    console.log('Cache manually cleared');
  }

  // Check if cache is empty
  isCacheEmpty() {
    return this.cache.size === 0 && this.loadedAssets.size === 0;
  }

  // Preload imported images (for webpack-processed assets)
  async preloadImportedImage(importedImage) {
    if (this.imageCache.has(importedImage)) {
      return this.imageCache.get(importedImage);
    }

    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        this.imageCache.set(importedImage, img);
        resolve(img);
      };
      img.onerror = () => {
        reject(new Error(`Failed to load imported image: ${importedImage}`));
      };
      img.src = importedImage;
    });
  }

  // Preload and cache images
  async preloadImage(src) {
    if (this.imageCache.has(src)) {
      return this.imageCache.get(src);
    }

    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        this.imageCache.set(src, img);
        resolve(img);
      };
      img.onerror = () => {
        reject(new Error(`Failed to load image: ${src}`));
      };
      img.src = src;
    });
  }

  // Preload multiple images
  async preloadImages(imageList) {
    const promises = imageList.map(src => this.preloadImage(src));
    try {
      await Promise.all(promises);
      console.log('All images preloaded successfully');
    } catch (error) {
      console.warn('Some images failed to preload:', error);
    }
  }

  // Cache data with key
  setCache(key, data) {
    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });
    // Save to sessionStorage immediately
    this.saveToSessionStorage();
  }

  // Get cached data
  getCache(key) {
    const cached = this.cache.get(key);
    if (cached) {
      return cached.data;
    }
    return null;
  }

  // Check if asset is loaded
  isAssetLoaded(key) {
    return this.loadedAssets.has(key);
  }

  // Mark asset as loaded
  markAssetLoaded(key) {
    this.loadedAssets.add(key);
    // Save to sessionStorage immediately
    this.saveToSessionStorage();
  }

  // Clear cache (useful for hard refresh)
  clearCache() {
    this.cache.clear();
    this.loadedAssets.clear();
    this.imageCache.clear();
    sessionStorage.removeItem('assetCache');
    console.log('Cache cleared');
  }

  // Get cache statistics
  getCacheStats() {
    return {
      cachedItems: this.cache.size,
      loadedAssets: this.loadedAssets.size,
      cachedImages: this.imageCache.size
    };
  }

  // Force refresh specific cache item
  refreshCacheItem(key) {
    this.cache.delete(key);
    this.loadedAssets.delete(key);
    console.log(`Cache item refreshed: ${key}`);
  }

  // Check cache age
  getCacheAge(key) {
    const cached = this.cache.get(key);
    if (cached) {
      return Date.now() - cached.timestamp;
    }
    return null;
  }

  // Get imported asset by name
  getImportedAsset(name) {
    return this.importedAssets[name];
  }

  // Get all imported assets
  getAllImportedAssets() {
    return this.importedAssets;
  }

  // Debug method to log cache state
  debugCache() {
    console.log('=== Asset Service Cache Debug ===');
    console.log('Cache entries:', Array.from(this.cache.keys()));
    console.log('Loaded assets:', Array.from(this.loadedAssets));
    console.log('Imported assets:', Object.keys(this.importedAssets));
    console.log('Cache stats:', this.getCacheStats());
    console.log('SessionStorage has cache:', !!sessionStorage.getItem('assetCache'));
  }
}

// Create singleton instance
const assetService = new AssetService();

// Preload all imported assets
const importedAssets = [
  missionBg,
  cropLogo,
  aimLogo,
  aimLogoAlt,
  domusLogo,
  defaultDoctor,
  patternSvg
];

// Preload public assets (from public folder)
const publicAssets = [
  '/hero-bg.jpg',
  // Add more public assets here
];

// Initialize asset preloading
assetService.preloadImages(publicAssets).catch(error => {
  console.warn('Some public assets failed to preload:', error);
});

assetService.preloadImages(importedAssets).catch(error => {
  console.warn('Some imported assets failed to preload:', error);
});

// Debug cache on load
setTimeout(() => {
  assetService.debugCache();
}, 1000);

export default assetService; 